'use client'

import Image from 'next/image'
import { ArrowRight } from 'lucide-react'

interface Step {
  step: number
  title: string
  description: string
  imageSrc?: string
  imageAlt?: string
}

const IMG = {
  projectBrief: '/images/howitworks/project-brief.jpg',
  compareProposals: '/images/howitworks/compare-proposals.jpg',
  handshake: '/images/howitworks/handshake.jpg',
  searchFilter: '/images/howitworks/search-filter.jpg',
  sendProposal: '/images/howitworks/send-proposal.jpg',
  workflow: '/images/howitworks/workflow.jpg',
} as const

const clientSteps: Step[] = [
  {
    step: 1,
    title: '프로젝트 등록',
    description: '요구사항·예산·기간 입력',
    imageSrc: IMG.projectBrief,
    imageAlt: 'Project brief illustration',
  },
  {
    step: 2,
    title: '견적 수신 & 비교',
    description: '조건별로 한 화면 비교',
    imageSrc: IMG.compareProposals,
    imageAlt: 'Compare proposals illustration',
  },
  {
    step: 3,
    title: '팀 선택 & 진행',
    description: '선택 후 바로 진행',
    imageSrc: IMG.handshake,
    imageAlt: 'Handshake collaboration illustration',
  },
]

const freelancerSteps: Step[] = [
  {
    step: 1,
    title: '프로필/키워드 등록',
    description: '강점과 키워드를 등록해 추천 기준을 설정',
    imageSrc: IMG.searchFilter,
    imageAlt: 'Search filter illustration',
  },
  {
    step: 2,
    title: '요청 도착·추천 받기',
    description: '조건이 맞는 요청을 알림과 추천으로 확인',
    imageSrc: IMG.sendProposal,
    imageAlt: 'Send proposal illustration',
  },
  {
    step: 3,
    title: '제안 제출·협의·채택',
    description: '채팅과 상태 변경으로 결정 흐름을 추적',
    imageSrc: IMG.workflow,
    imageAlt: 'Project workflow illustration',
  },
]

interface HowItWorksToggleProps {
  isVisible: boolean
}

export default function HowItWorksToggle({ isVisible }: HowItWorksToggleProps) {
  const steps = freelancerSteps

  return (
    <section
      className={`w-full bg-gradient-to-b from-white to-slate-50 px-4 py-16 sm:px-6 md:py-20 lg:px-8 transition-all duration-700 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
    >
      <div className="mx-auto max-w-6xl">
        <div className="mb-12 text-center">
          <h2 className="mb-3 text-3xl font-bold text-slate-900 md:text-4xl lg:text-5xl">
            프로필 등록부터 3단계로 시작하세요
          </h2>
          <p className="text-xs font-medium text-slate-500 md:hidden">좌우로 넘겨 전체 단계를 확인하세요</p>
        </div>

        {/* 카드 레일 */}
        <div className="relative">
          <div className="pointer-events-none absolute right-0 top-0 h-full w-10 bg-gradient-to-l from-slate-50 to-transparent md:hidden" />

          <div
            className={[
              'flex gap-4 overflow-x-auto pb-2 md:overflow-visible',
              'snap-x snap-mandatory md:snap-none',
              '[&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]',
              'md:grid md:grid-cols-3 md:gap-6',
            ].join(' ')}
          >
            {steps.map((step, index) => (
              <div key={step.step} className="relative">
                {/* 데스크톱 화살표 (마지막 카드 제외) */}
                {index < steps.length - 1 && (
                  <div className="hidden md:block absolute -right-3 top-1/2 -translate-y-1/2 z-10 text-slate-300">
                    <ArrowRight className="h-6 w-6" />
                  </div>
                )}

                <div
                  className={[
                    'snap-start shrink-0 w-[280px] sm:w-[300px] md:w-auto',
                    'group relative overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-md',
                    isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3',
                  ].join(' ')}
                  style={{ transitionDelay: isVisible ? `${index * 90}ms` : '0ms' }}
                >
                  {/* 브랜드 컬러 accent bar */}
                  <div className="absolute top-0 left-0 right-0 h-1 bg-blue-600" />

                  <div className="p-5">
                    {/* Step 배지 - 상단 고정 */}
                    <div className="mb-4">
                      <div className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700">
                        Step {step.step}
                      </div>
                    </div>

                    {/* 썸네일 */}
                    <div className="relative mb-4 h-[110px] w-full overflow-hidden rounded-xl bg-slate-100 ring-1 ring-slate-100">
                      {step.imageSrc ? (
                        <Image
                          src={step.imageSrc}
                          alt={step.imageAlt ?? ''}
                          fill
                          className="object-cover"
                          sizes="(max-width: 640px) 260px, 300px"
                          priority={index === 0}
                        />
                      ) : (
                        <div className="h-full w-full bg-gradient-to-br from-blue-50 via-white to-slate-50" />
                      )}
                      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-white/25 to-transparent" />
                    </div>

                    {/* 텍스트 영역 - 2줄 구조 */}
                    <div className="space-y-1.5">
                      <h3 className="text-lg font-bold leading-tight text-slate-900">{step.title}</h3>
                      <p className="text-sm font-medium text-slate-600 leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
